﻿import {defineStore} from "pinia";
import {
    AthumiService,
    CreatePaymentSession,
    CreatePostPaidSession,
    CreateSelfBillingSession,
    ProductIdDto,
    ProductLineDto,
    ProductLineMetaDataDto,
    SearchSessionsQuery,
    SessionDto,
    SessionType
} from "@/api/enterprise-gateway-api.proxy.ts";
import {useEnterpriseGatewayApiProxy} from "@/plugins/proxy-client-plugin.ts";
import {LoadingState} from "@/app/stores/loading-state.ts";
import {useErrorStore} from "@/app/stores/error-store.ts";
import {useUserStore} from "@/app/stores/user-store.ts";
import {PagedResult} from "@/app/shared/helpers/paged-result.ts";

export interface BasketItem {
    productCode: string;
    productDescription: string;
    extraInfo?: string;
    variant?: string;
    isSelfBilling: boolean;
    vatPercentage: number;
    priceExclVat: number;
    amount?: number;
    metaData?: Record<string, string>;
}

export const useSessionStore = defineStore('session-store', () => {
    const apiProxy = useEnterpriseGatewayApiProxy();
    const errorStore = useErrorStore();
    const userStore = useUserStore();
    const loadingState = new LoadingState();

    const toProductLineMetaData = (metaData?: Record<string, string>) => {
        if (!metaData) {
            return undefined;
        }

        return Object.entries(metaData).map(([key, value]) => new ProductLineMetaDataDto({ key, value }));
    };
    
    const createDirectPaymentSession = async (basketItems: BasketItem[], athumiService: AthumiService, callbackUrl: string) => {
        loadingState.startLoading();
        const { isCitizen, user } = userStore;
        try {
            const createPaymentRequest = new CreatePaymentSession({
                athumiService: athumiService,
                callBackUrl: callbackUrl,
                voOrgCode: isCitizen ? undefined : user.voOrgCode,
                name: isCitizen ? `${user.firstName} ${user.lastName}` : user.organisationName,
                subjectId: isCitizen ? user.id : undefined,
                firstName: isCitizen ? user.firstName : undefined,
                lastName: isCitizen ? user.lastName : undefined,
                transactionId: crypto.randomUUID(),
                products: basketItems.map((item) => new ProductLineDto({
                    productId: new ProductIdDto({
                        id: item.productCode,
                        salesLineReferenceId: crypto.randomUUID(),
                        variantCode: item.variant
                    }),
                    invoiceLineTextValue: item.productDescription,
                    extraInfo: item.extraInfo,
                    priceExclVat: item.priceExclVat,
                    vatPercentage: item.vatPercentage,
                    metaData: toProductLineMetaData(item.metaData),
                }))
            });
            return await apiProxy.payment_Create(createPaymentRequest);
        } catch (error) {
            loadingState.setError();
            await errorStore.setError(error)
        } finally {
            loadingState.stopLoading();
        }
    }

    const createPostPaidSession = async (basketItems: BasketItem[], athumiService: AthumiService, isFedNot: boolean, invoiceSettingsId?: string) => {
        loadingState.startLoading();
        const { user } = userStore;
        try {
            const request = new CreatePostPaidSession({
                athumiService: athumiService,
                voOrgCode: user.voOrgCode,
                invoiceSettingId: invoiceSettingsId,
                transactionId: crypto.randomUUID(),
                organisationName: user.organisationName,
                products: basketItems.map((item) => new ProductLineDto({
                    productId: new ProductIdDto({
                        id: item.productCode,
                        salesLineReferenceId: crypto.randomUUID(),
                        variantCode: item.variant,
                    }),
                    invoiceLineTextValue: item.productDescription,
                    extraInfo: item.extraInfo,
                    priceExclVat: item.priceExclVat,
                    vatPercentage: item.vatPercentage,
                    metaData: toProductLineMetaData(item.metaData),
                }))
            });
            return isFedNot 
                ? await apiProxy.invoice_CreatePostPaidSessionFedNot(request)
                : await apiProxy.invoice_CreatePostPaidSession(request);
        } catch (error) {
            loadingState.setError();
            await errorStore.setError(error)
        } finally {
            loadingState.stopLoading();
        }
    }

    const createSelfBillingSession = async (basketItems: BasketItem[], athumiService: AthumiService) => {
        loadingState.startLoading();
        const { user } = userStore;
        try {
            const request = new CreateSelfBillingSession({
                athumiService: athumiService,
                voOrgCode: user.voOrgCode,
                transactionId: crypto.randomUUID(),
                organisationName: user.organisationName,
                products: basketItems.map((item) => new ProductLineDto({
                    productId: new ProductIdDto({
                        id: item.productCode,
                        salesLineReferenceId: crypto.randomUUID(),
                        variantCode: item.variant,
                    }),
                    invoiceLineTextValue: item.productDescription,
                    extraInfo: item.extraInfo,
                    priceExclVat: item.priceExclVat,
                    vatPercentage: item.vatPercentage,
                    metaData: toProductLineMetaData(item.metaData),
                }))
            });
            return await apiProxy.invoice_CreateSelfBillingSession(request);
        } catch (error) {
            loadingState.setError();
            await errorStore.setError(error)
        } finally {
            loadingState.stopLoading();
        }
    }    
    
    const searchSessions = async (query: SearchSessionsQuery) => {
        loadingState.startLoading();
        try {
            const response = await apiProxy.operations_SessionsSearch(query);
            return PagedResult.fromResponse<SessionDto>(
                response.sessions ?? [],
                response.pageNumber ?? 1,
                response.pageSize ?? 1,
                response.totalCount ?? 0
            )
        } catch (error) {
            loadingState.setError();
            await errorStore.setError(error)
        } finally {
            loadingState.stopLoading();
        }
    }
     
    const sessionDetail = async (sessionId: string, sessionType: SessionType) => {
        loadingState.startLoading();
        try {
            return await apiProxy.operations_GetSession(sessionId, sessionType);
        } catch (error) {
            loadingState.setError();
            await errorStore.setError(error)
        } finally {
            loadingState.stopLoading();
        }
    }
    
    return {
        isLoading: loadingState.isLoading,
        errorLoading: loadingState.errorLoading,
        createDirectPaymentSession,
        createPostPaidSession,
        createSelfBillingSession,
        sessionDetail,
        searchSessions
    }
})

